import { Static, Type } from '@sinclair/typebox';
import { PublicOAuthCallbackResponseObject, PublicOAuthCallbackResponseSchema } from './oauth';
import {
  CalendarEventSyncStatus,
  CalendarSyncDirection,
  CalendarSyncProvider,
  CalendarSyncStatus,
} from './translation_namespace/calendar_sync_namespace';

export { CalendarEventSyncStatus, CalendarSyncDirection, CalendarSyncProvider, CalendarSyncStatus };

// ─── Available calendar (one entry of the picker) ────────────────────────────

export const CalendarSyncAvailableCalendarSchema = Type.Object({
  id: Type.String(),
  summary: Type.String(),
  primary: Type.Optional(Type.Boolean()),
  // `false` = calendrier en lecture seule (abonné, partagé en lecture) : impossible d'y
  // pousser les RDV. Absent sur les listes enregistrées avant l'ajout du champ.
  writable: Type.Optional(Type.Boolean()),
});
export type CalendarSyncAvailableCalendarObject = Static<typeof CalendarSyncAvailableCalendarSchema>;

// ─── Member connection (one row per team_member × provider) ──────────────────

export const CalendarSyncMemberConnectionSchema = Type.Object({
  provider: Type.Enum(CalendarSyncProvider),
  status: Type.Enum(CalendarSyncStatus),
  direction: Type.Enum(CalendarSyncDirection),
  account_email: Type.Optional(Type.String()),
  account_name: Type.Optional(Type.String()),
  active_calendar_id: Type.Optional(Type.String()),
  available_calendars: Type.Optional(Type.Array(CalendarSyncAvailableCalendarSchema)),
  last_sync_at_formatted: Type.Optional(
    Type.String({
      format: 'date-time',
      meta: { source_key: 'last_sync_at', with_time: true },
    }),
  ),
  last_error: Type.Optional(Type.String()),
});
export type CalendarSyncMemberConnectionObject = Static<typeof CalendarSyncMemberConnectionSchema>;

// ─── Member entry rendered in the agenda sync tab ────────────────────────────

export const CalendarSyncMemberSchema = Type.Object({
  team_member_id: Type.Integer(),
  firstname: Type.Optional(Type.String()),
  lastname: Type.Optional(Type.String()),
  /**
   * Raw `team_member.picture` S3 key. The frontend uses it as a round-avatar
   * source. Declared as `String` (not `Any`) so Swagger and the generated
   * frontend types stay precise.
   */
  picture: Type.Optional(Type.String()),
  can_edit: Type.Boolean(),
  /**
   * URL secrète du flux iCal (quiconque la détient lit l'agenda du membre) : renvoyée
   * uniquement quand `can_edit` est vrai.
   */
  ical_url: Type.Optional(Type.String()),
  ical_include_customer: Type.Boolean(),
  connections: Type.Array(CalendarSyncMemberConnectionSchema),
});
export type CalendarSyncMemberObject = Static<typeof CalendarSyncMemberSchema>;

// ─── List response (GET /app/calendar-sync) ──────────────────────────────────

export const CalendarSyncListResponseSchema = Type.Object({
  members: Type.Array(CalendarSyncMemberSchema),
  available_providers: Type.Array(Type.Enum(CalendarSyncProvider)),
});
export type CalendarSyncListResponseObject = Static<typeof CalendarSyncListResponseSchema>;

// ─── OAuth flow ──────────────────────────────────────────────────────────────

export const CalendarAuthLinkResponseSchema = Type.Object({
  url: Type.String(),
});
export type CalendarAuthLinkResponseObject = Static<typeof CalendarAuthLinkResponseSchema>;

export const CalendarAuthCallbackRequestSchema = Type.Object({
  code: Type.String({ minLength: 1 }),
  state: Type.String({ minLength: 1 }),
});
export type CalendarAuthCallbackRequestObject = Static<typeof CalendarAuthCallbackRequestSchema>;

// ─── Provider config (PUT /app/calendar-sync/:provider/config) ───────────────

export const UpdateCalendarProviderConfigRequestSchema = Type.Object({
  active_calendar_id: Type.String({ minLength: 1 }),
  direction: Type.Enum(CalendarSyncDirection),
});
export type UpdateCalendarProviderConfigRequestObject = Static<typeof UpdateCalendarProviderConfigRequestSchema>;

// ─── iCal config (PUT /app/calendar-sync/ical/:team_member_id) ───────────────

export const UpdateIcalConfigRequestSchema = Type.Object({
  include_customer_info: Type.Optional(Type.Boolean()),
  regenerate_hash: Type.Optional(Type.Boolean()),
});
export type UpdateIcalConfigRequestObject = Static<typeof UpdateIcalConfigRequestSchema>;

// ─── Generic message response ────────────────────────────────────────────────

export const CalendarSyncMessageResponseSchema = Type.Object({
  message: Type.String(),
});
export type CalendarSyncMessageResponseObject = Static<typeof CalendarSyncMessageResponseSchema>;

export const PublicCalendarAuthCallbackResponseSchema = PublicOAuthCallbackResponseSchema;
export type PublicCalendarAuthCallbackResponseObject = PublicOAuthCallbackResponseObject;
